import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { HttpService } from '@/features/api/http.service';
import { invalidAllProjectQueries } from '@/features/projects/services/project.api';
import { toastBackendError } from '@/features/api/utils/toastBackendError';
import type { IProject, IProjectResults } from '@/types/api';
import { GEOSPATIAL_LAYER_LIST, type GeospatialLayerList } from '../constants';

const LOCATIONS_PATH = '/locations';

const LOCATIONS_FACTORY_NAME = 'locations';

export const locationsKeyFactory = createQueryKeys(LOCATIONS_FACTORY_NAME, {
  getLocationGeospatial: (locationId: string, geospatialLayer?: GeospatialLayerList) => ({
    queryKey: [LOCATIONS_PATH, locationId, 'geospatial', geospatialLayer],
    queryFn: () =>
      HttpService.get<string, GeoJSON.FeatureCollection>(
        `${LOCATIONS_PATH}/${locationId}/geospatial`,
        new URLSearchParams({
          geospatial_layer_type: geospatialLayer ?? GEOSPATIAL_LAYER_LIST.MAX_AADT,
        }),
        {
          headers: {
            Accept: 'application/geo+json',
          },
        },
      ),
  }),
  getLocationResults: (locationId: string) => ({
    queryKey: [LOCATIONS_PATH, locationId, 'results'],
    queryFn: () =>
      HttpService.get<string, IProjectResults>(`${LOCATIONS_PATH}/${locationId}/results`),
  }),
  getProjectLocationsCSVResults: (projectId: string) => ({
    queryKey: [LOCATIONS_PATH, projectId, 'csv_results'],
    queryFn: () =>
      HttpService.get<string, Blob>(`${LOCATIONS_PATH}/projects/${projectId}/results`, undefined, {
        headers: {
          'Content-Type': 'text/csv',
        },
      }),
  }),
});

// Default layer is "max_aadt_geojson"
export function useLocationGeospatialQuery(
  id?: string,
  geospatialLayer?: GeospatialLayerList,
  options?: { enabled: boolean },
) {
  return useQuery({
    ...locationsKeyFactory.getLocationGeospatial(id!, geospatialLayer),
    ...options,
  });
}

/**
 * Fetches a single location's full results (location + metrics + optional
 * forecast) from `GET /locations/{id}/results` for the map location-detail
 * popup.
 *
 * Loaded ON DEMAND: disabled until a pin is selected (`enabled: !!id`), so no
 * per-location metrics are fetched before a click. Keyed by the location id so
 * clicking a different pin swaps the cache entry. The backend `LocationResults`
 * schema maps to the existing frontend `IProjectResults` type — no hand-written
 * DTO.
 *
 * `options.enabled` additionally gates the fetch — the caller passes `false`
 * while the id's compute run is still in flight, so the popup shows its
 * skeleton instead of hitting results that don't exist yet.
 */
export function useLocationResultsQuery(id?: string, options?: { enabled?: boolean }) {
  return useQuery({
    ...locationsKeyFactory.getLocationResults(id ?? ''),
    enabled: Boolean(id) && (options?.enabled ?? true),
  });
}

export function useGetProjectLocationsCSVMutation() {
  return useMutation({
    mutationFn: (projectId: string) =>
      HttpService.get<string, Blob>(`${LOCATIONS_PATH}/projects/${projectId}/results`, undefined, {
        headers: {
          Accept: 'text/csv',
          'Content-Type': 'text/csv',
        },
        responseType: 'blob',
      }),
    onSuccess: async () => {
      toast.success('Successfully downloaded your project CSV');
    },
    onError: (err) => {
      toastBackendError(err);
    },
  });
}

export function useAddLocationsMultipart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      formMetricsAndLocations,
      onProgress,
    }: {
      formMetricsAndLocations: FormData;
      onProgress?: (progress: number) => void;
    }) =>
      HttpService.postWithMultiPart<FormData, IProject>(
        `${LOCATIONS_PATH}/metrics`,
        formMetricsAndLocations,
        undefined,
        onProgress,
      ),
    onSuccess: async () => {
      await invalidAllProjectQueries(queryClient);
      toast.success('Locations uploaded — computing metrics…');
    },
    onError: (err) => {
      toastBackendError(err);
    },
  });
}
